/**
 * Inline Invite Prompt Component
 *
 * Shows contextual invite prompts when users are creating tasks.
 */

import React, { useState, useEffect } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { inviteReminderAPI } from '../api/inviteReminders';
import { InviteModal } from './InviteModal';
import { Button, Card } from './ui';
import { showError } from '../utils/toast';
import { unwrapResponse } from '../utils/unwrapResponse';
import { UsersIcon } from './Icons';
import { queryKeys } from '../hooks/queries/queryKeys';

interface InlineInvitePromptProps {
  organizationId: string;
  organizationName: string;
  variant: 'task-creation' | 'task-list-empty';
  onDismiss?: () => void;
}

export const InlineInvitePrompt: React.FC<InlineInvitePromptProps> = ({
  organizationId,
  organizationName,
  variant,
  onDismiss,
}) => {
  const [showInviteModal, setShowInviteModal] = useState(false);
  // Local override for dismiss/invite-sent — neither re-fetches the prompt
  // check, they just hide the banner for the rest of this mount, matching
  // pre-migration's setShouldShow(false).
  const [hiddenLocally, setHiddenLocally] = useState(false);

  const context = variant === 'task-creation' ? 'task_creation' : 'task_list_empty';

  const promptQuery = useQuery<boolean>({
    queryKey: queryKeys.inlineInvite.prompt(organizationId, context),
    queryFn: async () => {
      const response = await inviteReminderAPI.checkInlinePrompt(organizationId, context);
      return unwrapResponse<{ shouldShow: boolean }>(response.data).shouldShow;
    },
    enabled: !!organizationId,
    // Pre-migration had no retry wrapper — a single try/catch'd fetch.
    retry: false,
  });

  // Pre-migration console.error'd a failed check; preserved since useQuery
  // (v5) has no onError callback. shouldShow already stays false-ish
  // (query.data undefined) on error, matching the old catch's setShouldShow(false).
  useEffect(() => {
    if (promptQuery.error) {
      console.error('Failed to check inline prompt:', promptQuery.error);
    }
  }, [promptQuery.error]);

  // isPending (not isLoading) so a disabled query (no organizationId) stays
  // "loading" forever, matching pre-migration's loading:true initial state
  // that never flips when the effect's `if (organizationId)` guard is never
  // entered.
  const loading = promptQuery.isPending;
  const shouldShow = !hiddenLocally && promptQuery.data === true;

  const dismissMutation = useMutation({
    mutationFn: () => {
      const dismissContext = variant === 'task-creation' ? 'inline_task_creation' : 'inline_task_list_empty';
      return inviteReminderAPI.dismissPermanently(organizationId, dismissContext);
    },
    onSuccess: () => {
      setHiddenLocally(true);
      onDismiss?.();
    },
    onError: () => {
      showError('Something went wrong. Please try again.');
    },
  });

  const handleDismiss = () => {
    dismissMutation.mutate();
  };

  const handleInviteSent = () => {
    setShowInviteModal(false);
    setHiddenLocally(true);
  };

  if (loading || !shouldShow) return null;

  const messages = {
    'task-creation': 'Tasks are more fun with a partner! Invite someone to collaborate.',
    'task-list-empty': 'Get started by inviting a partner to work with.',
  };

  return (
    <>
      <Card tone="secondary" variant="bordered" radius="lg" padding="md" className="dark:bg-bg-tertiary dark:border-border-medium mb-6 flex items-center justify-between gap-4 flex-col md:flex-row md:items-center">
        <div className="flex items-center gap-3 flex-1 flex-col md:flex-row text-center md:text-left">
          <UsersIcon size={24} className="shrink-0" />
          <p className="m-0 text-text-secondary text-sm leading-snug">{messages[variant]}</p>
        </div>
        <div className="flex gap-3 shrink-0 flex-wrap justify-center md:justify-end">
          <Button
            variant="primary"
            size="sm"
            onClick={() => setShowInviteModal(true)}
          >
            Invite Partner
          </Button>
          <Button
            variant="text"
            size="sm"
            onClick={handleDismiss}
            className="text-text-tertiary"
          >
            Don't show again
          </Button>
        </div>
      </Card>

      {showInviteModal && (
        <InviteModal
          isOpen={showInviteModal}
          onClose={() => setShowInviteModal(false)}
          organizationId={organizationId}
          organizationName={organizationName}
          onInviteSent={handleInviteSent}
        />
      )}
    </>
  );
};
